import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { GovTooltip } from '@gov-design-system-ce/react';
import type { DatovkaApiType } from '@gov-nx/api';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import { pipe } from '@gov-nx/core/types';
import {
	dataBoxLabel,
	getDataBoxIcon,
	getDataBoxInitials,
	useDataBoxCredit,
	useDataBoxesContext,
	useDataBoxLabel,
} from '@gov-nx/module/data-box';
import { PageCode } from '@gov-nx/module/page';
import { Button, Chip, CopyButton, Icon, LayoutGap } from '@gov-nx/ui/web';
import { formatPrice, limitNumber } from '@gov-nx/utils/common';
import { DataBoxItemNavigation } from './DataBoxItemNavigation';

interface TruncateNameProps {
	name: string;
}

const TruncateName = ({ name }: TruncateNameProps) => {
	const textRef = useRef<HTMLHeadingElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	useEffect(() => {
		const dataBoxName = textRef.current;
		if (dataBoxName) {
			if (dataBoxName.scrollHeight > dataBoxName.clientHeight) {
				setIsTruncated(true);
			}
		}
	}, []);

	return (
		<>
			{isTruncated ? (
				<GovTooltip placement="bottom">
					<GovTooltip.Trigger>
						<h2
							ref={textRef}
							className="line-clamp-2 text-body-l-bold!">
							{name}
						</h2>
					</GovTooltip.Trigger>
					<GovTooltip.Content size="s">{name}</GovTooltip.Content>
				</GovTooltip>
			) : (
				<h2
					ref={textRef}
					className="line-clamp-2 text-body-l-bold!">
					{name}
				</h2>
			)}
		</>
	);
};

interface DataBoxesListProps {
	dataBox: DatovkaApiType;
	toggleable: boolean;
	defaultlyOpen?: boolean;
}

export const DataBoxesItem = ({
	dataBox,
	toggleable,
	defaultlyOpen = false,
}: DataBoxesListProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tn = getLocalizeCurried(PageCode['datove-schranky']);
	const { label } = useDataBoxLabel();

	const {
		handleCreditModalOpen,
		mobileMenuDataBox,
		setMobileMenuDataBox,
		getUnreadMessagesCounts,
	} = useDataBoxesContext();

	const { credit } = useDataBoxCredit(dataBox.datovaSchrankaId);

	const unreadMessagesCount = dataBox.datovaSchrankaId
		? getUnreadMessagesCounts(dataBox.datovaSchrankaId)
		: null;

	const [isMenuItemOpen, setIsMenuItemOpen] = useState(defaultlyOpen);

	const name = dataBoxLabel(dataBox, true);

	const toggleContent = (
		<>
			{dataBox.typSchranky && (
				<div className="text-left text-body-s text-(--text-secondary)">
					{label(dataBox.typSchranky)}
				</div>
			)}
			{!dataBox.odpojeno && !isMenuItemOpen && toggleable ? (
				!unreadMessagesCount ? (
					<Chip
						color="success"
						size="s"
						type="outlined">
						<span>{tn('zpravy.vsechny-prectene')}</span>
					</Chip>
				) : (
					<Chip
						color="warning"
						size="s"
						type="solid">
						<span>
							{tn('zpravy.neprectene-pocet', { count: unreadMessagesCount })}
						</span>
					</Chip>
				)
			) : null}
			{dataBox.odpojeno && (
				<GovTooltip placement="right">
					<GovTooltip.Trigger className="text-left max-w-fit">
						<Chip
							color="error"
							size="s"
							type="outlined">
							<span>{tn('schranky.odpojena')}</span>
						</Chip>
					</GovTooltip.Trigger>
					<GovTooltip.Content size="s">
						<p>{tn('schranky.odpojena-slozky')}</p>
					</GovTooltip.Content>
				</GovTooltip>
			)}
		</>
	);

	return (
		<>
			<Button
				color="primary"
				type="link"
				size="m"
				className="block relative p-1! ds-no-underline [ xl:hidden! ]"
				aria-label={tn('wcag.nabidka-schranky') + ' ' + name}
				aria-expanded={true}
				aria-controls={'ds-menu-' + dataBox.datovaSchrankaId}
				onClick={() =>
					setMobileMenuDataBox(dataBox.datovaSchrankaId as string)
				}>
				<div
					className={cx(
						'flex flex-col justify-center items-center w-10 h-10 rounded-full bg-(--background-block-primary) text-(--text-primary)',
						dataBox.datovaSchrankaId === mobileMenuDataBox && 'ds-active'
					)}>
					{pipe(
						getDataBoxIcon(
							dataBox?.typSchranky as DatovkaApiType['typSchranky']
						),
						(icon) =>
							icon ? (
								<Icon
									name={icon}
									type="templates"
									size="xs"
									className="relative top-0.5"
								/>
							) : null
					)}
					<h2
						className="text-body-s-bold! ds-no-underline"
						aria-label={name}>
						{getDataBoxInitials(dataBox)}
					</h2>
					{!dataBox.odpojeno && !!unreadMessagesCount && (
						<span
							className="absolute top-0 right-0 flex items-center px-1 h-4 text-body-xs! bg-(--background-status-warning) text-(--text-primary-color) rounded-full"
							aria-label={tn('zpravy.neprectene-zpravy')}>
							{limitNumber(unreadMessagesCount)}
						</span>
					)}
				</div>
			</Button>

			{toggleable ? (
				<Button
					color="primary"
					type="base"
					size="l"
					isExpanded
					className="hidden! ds-toggle-name [ xl:block! xl:m-1! ]"
					onClick={() => setIsMenuItemOpen((isMenuItemOpen) => !isMenuItemOpen)}
					aria-label={tn('wcag.nabidka-schranky') + ' ' + name}>
					<div className="flex flex-col gap-(--spacing-s) w-full">
						<div className="flex justify-between items-start gap-(--spacing-xs) text-left text-(--text-primary-color)">
							<TruncateName name={name} />
							<Icon
								name={isMenuItemOpen ? 'chevron-up' : 'chevron-down'}
								type="components"
								className="mt-1!"
							/>
						</div>
						{toggleContent}
					</div>
				</Button>
			) : (
				<div className="hidden! p-2 [ xl:block! ]">
					<div className="flex justify-between items-start gap-1 text-left text-headline-xs text-(--text-primary-color)">
						<TruncateName name={name} />
					</div>
					{toggleContent}
				</div>
			)}

			<div
				id={'ds-menu-' + dataBox.datovaSchrankaId}
				className={cx(
					'absolute top-0 left-[72px] w-[244px] h-[calc(100vh-65px)] bg-(--background-block-primary) [ sm:h-[calc(100vh-73px)] ] [ xl:relative xl:left-0 xl:w-auto xl:h-auto xl:bg-transparent xl:flex xl:flex-col xl:gap-(--templates-margin-m) ]',
					mobileMenuDataBox !== dataBox.datovaSchrankaId && 'hidden',
					toggleable && !isMenuItemOpen && '[ xl:hidden! ]'
				)}>
				<div className="p-(--templates-margin-l) [ xl:hidden! ]">
					<div className="flex justify-between items-start gap-1 text-left text-headline-xs text-(--text-primary-color)">
						<span>{name}</span>
						<Button
							color="primary"
							type="base"
							size="s"
							aria-label={tn('wcag.zavrit-nabidku')}
							onClick={() => setMobileMenuDataBox(null)}
							iconStart={
								<Icon
									name="x-lg"
									type="components"
								/>
							}
						/>
					</div>
					{dataBox.typSchranky && (
						<div className="text-body-s text-(--text-secondary)">
							{label(dataBox.typSchranky)}
						</div>
					)}
				</div>
				<LayoutGap
					gap="m"
					className="p-(--templates-margin-l)">
					<dl className="text-body-xs text-(--text-secondary)">
						<div className="flex items-center">
							<dt className="mr-1!">{tn('schranky.id-schranky')}</dt>
							<dd>
								<strong>{dataBox.datovaSchrankaId}</strong>
							</dd>
							<CopyButton
								label={tn('schranky.zkopirovat-id-schranky')}
								value={dataBox.datovaSchrankaId as string}
							/>
						</div>
						{!dataBox.odpojeno && credit !== null && (
							<div className="flex items-center">
								<dt className="mr-1!">{tn('kredit.kredit')}</dt>
								<dd>
									<strong>{formatPrice(credit)}</strong>
								</dd>
								<Button
									color="primary"
									type="base"
									size="s"
									onClick={() =>
										handleCreditModalOpen(dataBox.datovaSchrankaId as string)
									}
									aria-label={tn('kredit.wcag.dobit')}>
									{tn('kredit.dobit')}
								</Button>
							</div>
						)}
					</dl>

					<DataBoxItemNavigation
						dataBox={dataBox}
						unreadMessagesCount={unreadMessagesCount}
					/>
				</LayoutGap>
			</div>
		</>
	);
};
